import { toast } from 'sonner';

/**
 * Check if an IP address is within a CIDR range
 */
function ipToNumber(ip: string): number {
  return ip.split('.').reduce((acc, octet) => (acc << 8) + parseInt(octet), 0) >>> 0;
}

function cidrToRange(cidr: string): { start: number; end: number } {
  const [ip, bits] = cidr.split('/');
  const mask = ~((1 << (32 - parseInt(bits))) - 1);
  const start = ipToNumber(ip) & mask;
  const end = start + ~mask;
  return { start: start >>> 0, end: end >>> 0 };
}

function isIpInRange(ip: string, cidr: string): boolean {
  const ipNum = ipToNumber(ip);
  const range = cidrToRange(cidr);
  return ipNum >= range.start && ipNum <= range.end;
}

/**
 * Get the user's current IP address
 */
export async function getCurrentIp(): Promise<string | null> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch (error) {
    console.error('Failed to get IP address:', error);
    return null;
  }
}

/**
 * Verify if the current IP is within allowed ranges
 */
export async function verifyIpAddress(allowedRanges: string[]): Promise<boolean> {
  if (!allowedRanges || allowedRanges.length === 0) {
    return true;
  }

  const currentIp = await getCurrentIp();
  if (!currentIp) {
    toast.error('Unable to verify your network. Please check your internet connection.');
    return false;
  }

  const isAllowed = allowedRanges.some(range => isIpInRange(currentIp, range));
  
  if (!isAllowed) {
    toast.error('You must be connected to the company network to check in.');
  }
  
  return isAllowed;
}

/**
 * Get the current WiFi SSID (mobile only via Capacitor)
 * Note: This feature requires the @capawesome-team/capacitor-wifi plugin
 * to be installed and properly configured on the mobile app
 */
export async function getCurrentWifiSsid(): Promise<string | null> {
  try {
    // Check if we're on a mobile platform with Capacitor
    if (typeof window !== 'undefined' && (window as any).Capacitor) {
      const { Capacitor } = await import('@capacitor/core');
      
      if (Capacitor.isNativePlatform()) {
        // WiFi plugin is optional - check if it's available
        console.warn('WiFi SSID verification requires @capawesome-team/capacitor-wifi plugin to be installed');
        return null;
      }
    }
    
    // WiFi SSID detection not available on web or plugin not installed
    return null;
  } catch (error) {
    console.error('Failed to get WiFi SSID:', error);
    return null;
  }
}

/**
 * Verify if the current WiFi SSID is allowed
 */
export async function verifyWifiSsid(allowedSsids: string[]): Promise<boolean> {
  if (!allowedSsids || allowedSsids.length === 0) {
    return true;
  }

  const currentSsid = await getCurrentWifiSsid();
  
  if (!currentSsid) {
    toast.error('WiFi verification requires the mobile app. Please use the company network or install the mobile app.');
    return false;
  }

  const isAllowed = allowedSsids.some(ssid => 
    currentSsid.toLowerCase() === ssid.toLowerCase()
  );
  
  if (!isAllowed) {
    toast.error(`You must be connected to an approved WiFi network. Current: ${currentSsid}`);
  }
  
  return isAllowed;
}

/**
 * Perform network verification based on event settings
 */
export async function verifyNetwork(
  mode: 'ip_only' | 'wifi_only' | 'ip_or_wifi' | 'ip_and_wifi',
  allowedIpRanges?: string[],
  allowedWifiSsids?: string[]
): Promise<boolean> {
  switch (mode) {
    case 'ip_only':
      return await verifyIpAddress(allowedIpRanges || []);
    
    case 'wifi_only':
      return await verifyWifiSsid(allowedWifiSsids || []);
    
    case 'ip_or_wifi':
      const ipValid = await verifyIpAddress(allowedIpRanges || []);
      if (ipValid) return true;
      return await verifyWifiSsid(allowedWifiSsids || []);
    
    case 'ip_and_wifi':
      const ipCheck = await verifyIpAddress(allowedIpRanges || []);
      const wifiCheck = await verifyWifiSsid(allowedWifiSsids || []);
      return ipCheck && wifiCheck;
    
    default:
      return true;
  }
}
